import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, ShieldCheck, Clock, Lock, Crown, Star, ArrowRight, Gift, Cpu,
} from "lucide-react";
import { fetchProgramasLicenciables, fetchMiLicencia, fetchMiMembresia } from "../lib/pagos";
import { usePlanFeatures } from "../hooks/usePlanFeatures";
import { useAuth } from "../context/AuthContext";

const PLAN_LABEL = { basico: "Básico", pro: "Pro", elite: "Premium" };
const PLAN_ICON = { basico: Star, pro: ArrowRight, elite: Crown };
const PLAN_COLOR = { basico: "#64748B", pro: "#2563EB", elite: "#B45309" };

function fmtFecha(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-PE", { day: "2-digit", month: "short", year: "numeric" });
}

// Ruta interna del programa (mismo criterio que ProgramaCard). Sin cambiar rutas/lógica.
function rutaEntrar(p) {
  const key = `${p.slug || ""} ${p.name || ""}`.toLowerCase();
  if (key.includes("valorizacion")) return "/software/corex-valorizaciones";
  if (key.includes("vivienda") || key.includes("multifamiliar")) return "/software/vivienda-multifamiliar";
  return p.app_url || `/software`;
}

export default function EstadoHerramientas() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { isBasico } = usePlanFeatures();

  const [programas, setProgramas] = useState(null);
  const [licencias, setLicencias] = useState({});
  const [membresia, setMembresia] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      fetchProgramasLicenciables().catch(() => []),
      fetchMiMembresia().catch(() => null),
    ]).then(async ([progs, mem]) => {
      if (!alive) return;
      setMembresia(mem);
      const lista = progs || [];
      setProgramas(lista);
      const pares = await Promise.all(
        lista.map((p) =>
          fetchMiLicencia(p.slug).then((l) => [p.slug, l]).catch(() => [p.slug, null])
        )
      );
      if (!alive) return;
      setLicencias(Object.fromEntries(pares));
    });
    return () => { alive = false; };
  }, []);

  const entrar = (p) => {
    const r = rutaEntrar(p);
    if (r.startsWith("/")) navigate(r);
    else window.open(r, "_blank", "noopener,noreferrer");
  };
  const comprarLicencia = (slug) => navigate(`/software/licencia/${slug}`);

  const planId = membresia?.plan || "basico";
  const PlanIcon = isAdmin ? ShieldCheck : (PLAN_ICON[planId] || Star);
  const planColor = isAdmin ? "#1e293b" : (PLAN_COLOR[planId] || "#64748B");

  return (
    <section className="mb-10" data-testid="mis-programas-licencias">
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-xl bg-indigo-100 flex items-center justify-center">
          <Cpu size={22} className="text-indigo-600" />
        </span>
        <div>
          <h2 className="text-xl font-extrabold text-slate-900 tracking-tight">Mis Programas y Licencias</h2>
          <p className="text-sm text-slate-500">Estado de acceso a tus herramientas técnicas COREX.</p>
        </div>
      </div>

      {/* Resumen de membresía */}
      <div
        className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border border-slate-100 bg-white shadow-soft px-5 py-4"
        data-testid="resumen-membresia"
      >
        <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${planColor}1a` }}>
          <PlanIcon size={18} style={{ color: planColor }} />
        </span>
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Tu membresía</div>
          <div className="text-base font-extrabold text-slate-900" data-testid="membresia-plan">
            {isAdmin ? "Administrador" : `Plan ${PLAN_LABEL[planId] || "Básico"}`}
          </div>
        </div>
        <div className="hidden sm:block h-9 w-px bg-slate-100" />
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Vigencia</div>
          <div className="text-sm font-semibold text-slate-700" data-testid="membresia-vigencia">
            {isAdmin ? "Acceso total" : (membresia?.vigente_hasta ? `Hasta ${fmtFecha(membresia.vigente_hasta)}` : "Sin vencimiento")}
          </div>
        </div>
        <button
          onClick={() => navigate("/planes")}
          data-testid="membresia-ver-btn"
          className="ml-auto inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition-colors"
        >
          <Star size={15} className="text-amber-500" /> VER MEMBRESÍAS
        </button>
      </div>

      {/* Programas licenciables */}
      {programas === null ? (
        <div className="mt-6 flex items-center gap-2 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Cargando tus programas…
        </div>
      ) : programas.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-slate-100 bg-white shadow-soft p-8 text-center" data-testid="programas-vacio">
          <Cpu size={36} className="mx-auto text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">Aún no hay programas licenciables disponibles.</p>
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
          {programas.map((p) => {
            const lic = licencias[p.slug] || null;
            const origen = lic?.origen || null;
            const estado = isAdmin ? "admin" : (lic?.estado || "no_tiene");
            const esAdminOtorgada = origen === "ADMINISTRADOR";

            return (
              <div
                key={p.slug}
                className="bg-white rounded-2xl border border-slate-100 shadow-soft p-5 flex flex-col"
                data-testid={`programa-lic-${p.slug}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-lg font-bold text-slate-900">{p.name}</h3>
                  {/* Badge de estado */}
                  {estado === "admin" && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full bg-slate-800 text-white" data-testid={`badge-${p.slug}`}>
                      <ShieldCheck size={12} /> Acceso total
                    </span>
                  )}
                  {estado === "vigente" && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700" data-testid={`badge-${p.slug}`}>
                      <ShieldCheck size={12} /> Vigente
                    </span>
                  )}
                  {estado === "vencida" && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full bg-amber-100 text-amber-700" data-testid={`badge-${p.slug}`}>
                      <Clock size={12} /> Vencida
                    </span>
                  )}
                  {estado === "no_tiene" && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full bg-slate-100 text-slate-500" data-testid={`badge-${p.slug}`}>
                      <Lock size={12} /> Sin licencia
                    </span>
                  )}
                </div>

                {/* Cuerpo por estado */}
                <div className="mt-3 flex-1 text-sm">
                  {estado === "admin" && (
                    <p className="text-slate-500" data-testid={`nota-${p.slug}`}>
                      Como administrador tienes acceso completo a este programa. No requiere licencia.
                    </p>
                  )}

                  {estado === "vigente" && (
                    <div className="space-y-1" data-testid={`nota-${p.slug}`}>
                      <p className="text-emerald-700 font-semibold">
                        Licencia activa · {lic?.dias_restantes ?? 0} días restantes
                      </p>
                      <p className="text-slate-500">Vence el {fmtFecha(lic?.fecha_vencimiento)}</p>
                      {esAdminOtorgada ? (
                        <p className="flex items-center gap-1.5 text-[12px] text-indigo-600" data-testid={`origen-${p.slug}`}>
                          <Gift size={13} /> Acceso otorgado como beneficio por ConstruLink.
                        </p>
                      ) : (
                        <p className="text-[12px] text-slate-400" data-testid={`origen-${p.slug}`}>
                          Licencia adquirida vía Mercado Pago.
                        </p>
                      )}
                    </div>
                  )}

                  {estado === "vencida" && (
                    <div className="space-y-1" data-testid={`nota-${p.slug}`}>
                      <p className="text-amber-700 font-semibold">Regresó a capacidades de membresía</p>
                      <p className="text-slate-500">Tu licencia venció el {fmtFecha(lic?.fecha_vencimiento)}. Renuévala para recuperar el acceso completo.</p>
                      {esAdminOtorgada && (
                        <p className="flex items-center gap-1.5 text-[12px] text-indigo-600" data-testid={`origen-${p.slug}`}>
                          <Gift size={13} /> El acceso anterior fue un beneficio otorgado por ConstruLink.
                        </p>
                      )}
                    </div>
                  )}

                  {estado === "no_tiene" && (
                    <p className="text-slate-500" data-testid={`nota-${p.slug}`}>
                      Acceso limitado{isBasico ? " (plan Básico · Fase A)" : ""}. Adquiere una licencia para activar todas las capacidades del programa.
                    </p>
                  )}
                </div>

                {/* CTA por estado */}
                <div className="mt-4">
                  {estado === "admin" && (
                    <button
                      onClick={() => entrar(p)}
                      data-testid={`entrar-${p.slug}`}
                      className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white font-semibold text-sm px-4 py-2 rounded-xl transition-colors"
                    >
                      <ArrowRight size={15} /> ENTRAR AL PROGRAMA
                    </button>
                  )}
                  {estado === "vigente" && (
                    <button
                      onClick={() => entrar(p)}
                      data-testid={`entrar-${p.slug}`}
                      className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm px-4 py-2 rounded-xl transition-colors"
                    >
                      <ArrowRight size={15} /> ENTRAR AL PROGRAMA
                    </button>
                  )}
                  {estado === "vencida" && (
                    <button
                      onClick={() => comprarLicencia(p.slug)}
                      data-testid={`renovar-${p.slug}`}
                      className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold text-sm px-4 py-2 rounded-xl transition-colors"
                    >
                      <Clock size={15} /> RENOVAR
                    </button>
                  )}
                  {estado === "no_tiene" && (
                    <button
                      onClick={() => comprarLicencia(p.slug)}
                      data-testid={`comprar-${p.slug}`}
                      className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm px-4 py-2 rounded-xl transition-colors"
                    >
                      <Crown size={15} /> COMPRAR LICENCIA
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
